/**
 * Leviathan Web - Shipment Detail Page
 * Quasar Prime LLC
 * View and manage individual shipment with status workflow
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Truck,
  User,
  Clock,
  MapPin,
  Package,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Play,
  FileText,
  Phone,
  Trash2,
  Database,
  Printer,
  Send,
  TrendingUp,
  Lock,
  Unlock,
  Edit3,
  Upload,
  ExternalLink,
  ShieldCheck,
  Receipt,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';
import LiftedAtCell from '@/components/shipments/LiftedAtCell';

interface ShipmentItem {
  id: string;
  productId: string;
  orderedQuantity: number;
  shippedQuantity: number;
  deliveredQuantity: number | null;
  unit: string;
  unitPrice: number;
  lineTotal: number;
  sourceTankId: string | null;
  originTerminal?: { id: string; name: string; state: string | null } | null;
  liftingItem?: {
    id: string;
    lifting: { liftingNumber: string; terminal: { id: string; name: string; state: string | null } | null };
  } | null;
  product: {
    id: string;
    name: string;
    sku: string;
    unit: string;
  };
}

interface Shipment {
  id: string;
  shipmentNumber: string;
  bolNumber: string | null;
  bolDocumentUrl: string | null;
  invoiceId: string | null;
  status: string;
  shipDate: string;
  deliveryDate: string | null;
  deliveredQuantity: number | null;
  deliveryAddress: string | null;
  deliveryCity: string | null;
  deliveryState: string | null;
  deliveryZip: string | null;
  receivedBy: string | null;
  signature: string | null;
  proofOfDelivery: string | null;
  notes: string | null;
  deliveryNotes: string | null;
  createdAt: string;
  order: {
    id: string;
    orderNumber: string;
    total: number;
    priority: string;
    poNumber: string | null;
    items: Array<{
      id: string;
      quantity: number;
      unitPrice: number;
      lineTotal: number;
      product: {
        id: string;
        name: string;
        sku: string;
        unit: string;
      };
    }>;
  };
  dispatch: {
    id: string;
    dispatchNumber: string;
    status: string;
  } | null;
  customer: {
    id: string;
    name: string;
    customerNumber: string;
    phone: string | null;
    email: string | null;
  };
  truck: {
    id: string;
    unitNumber: string;
    name: string | null;
    totalCapacity: number | null;
    make: string | null;
    model: string | null;
    licensePlate: string | null;
  } | null;
  driver: {
    id: string;
    driverNumber: string;
    firstName: string;
    lastName: string;
    phone: string | null;
  } | null;
  sourceTank: {
    id: string;
    tankNumber: string;
    name: string;
    product: {
      name: string;
    } | null;
  } | null;
  items: ShipmentItem[];
}

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    color: string;
    bgColor: string;
    icon: React.ElementType;
    nextStatus: string | null;
    nextAction: string | null;
  }
> = {
  PENDING: {
    label: 'Pending',
    color: 'text-steel',
    bgColor: 'bg-steel/20 border-steel/50',
    icon: Clock,
    nextStatus: 'IN_TRANSIT',
    nextAction: 'Start Transit',
  },
  IN_TRANSIT: {
    label: 'In Transit',
    color: 'text-amber',
    bgColor: 'bg-amber/20 border-amber/50',
    icon: Truck,
    nextStatus: 'DELIVERED',
    nextAction: 'Confirm Delivery',
  },
  DELIVERED: {
    label: 'Delivered',
    color: 'text-success-green',
    bgColor: 'bg-success-green/20 border-success-green/50',
    icon: CheckCircle,
    nextStatus: 'VERIFIED',
    nextAction: 'Verify Quantities',
  },
  PARTIAL: {
    label: 'Partial',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-400/20 border-yellow-400/50',
    icon: TrendingUp,
    nextStatus: 'VERIFIED',
    nextAction: 'Verify Quantities',
  },
  FAILED: {
    label: 'Failed',
    color: 'text-error-red',
    bgColor: 'bg-error-red/20 border-error-red/50',
    icon: AlertTriangle,
    nextStatus: 'PENDING',
    nextAction: 'Retry Shipment',
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'text-text-tertiary',
    bgColor: 'bg-panel-border/20 border-panel-border/50',
    icon: XCircle,
    nextStatus: null,
    nextAction: null,
  },
  VERIFIED: {
    label: 'Verified',
    color: 'text-blue-400',
    bgColor: 'bg-blue-400/20 border-blue-400/50',
    icon: ShieldCheck,
    nextStatus: 'POSTED',
    nextAction: 'Post Shipment',
  },
  POSTED: {
    label: 'Posted',
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/20 border-purple-400/50',
    icon: Lock,
    nextStatus: 'INVOICED',
    nextAction: 'Create Invoice',
  },
  INVOICED: {
    label: 'Invoiced',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-400/20 border-emerald-400/50',
    icon: Receipt,
    nextStatus: null,
    nextAction: null,
  },
};

/**
 * Editable statuses - shipment can be modified in these states
 */
const EDITABLE_STATUSES = ['PENDING', 'IN_TRANSIT', 'DELIVERED', 'PARTIAL', 'VERIFIED'];

/**
 * Check if a shipment is editable based on its status
 */
function isShipmentEditable(status: string): boolean {
  return EDITABLE_STATUSES.includes(status);
}

const ShipmentDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAttachBolModal, setShowAttachBolModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [linkedBolId, setLinkedBolId] = useState<string | null>(null);

  // Delivery form state
  const [deliveredQuantity, setDeliveredQuantity] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [bolDocumentUrl, setBolDocumentUrl] = useState('');

  useEffect(() => {
    fetchShipment();
  }, [id, accessToken]);

  const fetchShipment = async () => {
    try {
      const response = await fetch(`${API_URL}/api/shipments/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.ok) {
        const data = await response.json();
        setShipment(data);

        // Look up linked BOL if bolNumber exists but no document URL
        if (data.bolNumber && !data.bolDocumentUrl) {
          try {
            const bolResponse = await fetch(
              `${API_URL}/api/bol/number/${encodeURIComponent(data.bolNumber)}`,
              { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            if (bolResponse.ok) {
              const bolData = await bolResponse.json();
              setLinkedBolId(bolData.id);
            }
          } catch {
            // BOL lookup is best-effort
          }
        }
      } else {
        setError('Shipment not found');
      }
    } catch (err) {
      setError('Failed to load shipment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!shipment) return;

    setIsUpdating(true);
    try {
      const payload: any = { status: newStatus };

      if (newStatus === 'DELIVERED' || newStatus === 'PARTIAL') {
        payload.deliveredQuantity = deliveredQuantity || null;
        payload.receivedBy = receivedBy || null;
        payload.deliveryNotes = deliveryNotes || null;
      }

      if (newStatus === 'CANCELLED' || newStatus === 'FAILED') {
        payload.deliveryNotes = cancelReason || deliveryNotes || null;
      }

      const response = await fetch(`${API_URL}/api/shipments/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update status');
      }

      await fetchShipment();
      setShowDeliveryModal(false);
      setShowCancelModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/api/shipments/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete shipment');
      }

      navigate('/shipments');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete shipment');
      setShowDeleteModal(false);
    }
  };

  const handleVerify = async () => {
    setIsUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/shipments/${id}/verify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to verify shipment');
      }

      await fetchShipment();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify shipment');
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePost = async () => {
    setIsUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/shipments/${id}/post`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to post shipment');
      }

      await fetchShipment();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to post shipment');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUnpost = async () => {
    setIsUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/shipments/${id}/unpost`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to unpost shipment');
      }

      await fetchShipment();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unpost shipment');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAttachBol = async () => {
    if (!bolDocumentUrl.trim()) {
      setError('Please enter a BOL document URL');
      return;
    }

    setIsUpdating(true);
    try {
      const response = await fetch(`${API_URL}/api/shipments/${id}/attach-bol`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ bolDocumentUrl }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to attach BOL');
      }

      await fetchShipment();
      setShowAttachBolModal(false);
      setBolDocumentUrl('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to attach BOL');
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePrintDeliveryTicket = () => {
    window.print();
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center space-x-4">
          <div className="w-3 h-3 bg-amber animate-pulse" />
          <span className="text-text-secondary font-mono">Loading shipment...</span>
        </div>
      </div>
    );
  }

  if (error || !shipment) {
    return (
      <div className="text-center py-20">
        <AlertTriangle className="w-16 h-16 text-error-red mx-auto mb-4" />
        <p className="text-error-red font-mono mb-4">{error || 'Shipment not found'}</p>
        <Link to="/shipments" className="text-amber font-mono hover:underline">
          &larr; Back to Shipments
        </Link>
      </div>
    );
  }

  const statusConfig = (STATUS_CONFIG[shipment.status] || STATUS_CONFIG.PENDING)!;
  const StatusIcon = statusConfig.icon;
  const totalShipped = shipment.items.reduce((sum, item) => sum + item.shippedQuantity, 0);
  const totalDelivered = shipment.deliveredQuantity ||
    shipment.items.reduce((sum, item) => sum + (item.deliveredQuantity || 0), 0);
  const totalValue = shipment.items.reduce((sum, item) => sum + item.lineTotal, 0);
  const canCancel = ['PENDING', 'IN_TRANSIT'].includes(shipment.status);
  const canDelete = ['PENDING', 'CANCELLED'].includes(shipment.status);
  const isEditable = isShipmentEditable(shipment.status);
  const canUnpost = shipment.status === 'POSTED' && !shipment.invoiceId;

  return (
    <div className="space-y-6">
      {/* Print-only Delivery Ticket Header */}
      <div className="hidden print:block print-ticket-header">
        <div className="text-center mb-6 pb-4" style={{ borderBottom: '3px solid black' }}>
          <h1 style={{ fontSize: '24pt', fontWeight: 'bold', margin: 0 }}>DELIVERY TICKET</h1>
          <p style={{ fontSize: '10pt', marginTop: '4px' }}>Leviathan Fuel Management System</p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div>
            <p style={{ fontWeight: 'bold', fontSize: '14pt' }}>{shipment.shipmentNumber}</p>
            <p>Ship Date: {formatDate(shipment.shipDate)}</p>
            {shipment.bolNumber && <p>BOL: {shipment.bolNumber}</p>}
            {shipment.order.poNumber && <p>PO: {shipment.order.poNumber}</p>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontWeight: 'bold' }}>Order: {shipment.order.orderNumber}</p>
            <p>Status: {statusConfig.label}</p>
            {shipment.deliveryDate && <p>Delivered: {formatDate(shipment.deliveryDate)}</p>}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-4 mb-2">
                <Link
                  to="/shipments"
                  className="text-text-secondary hover:text-amber transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Link>
                <span
                  className={`px-3 py-1 text-sm font-mono border ${statusConfig.bgColor} ${statusConfig.color}`}
                  style={{ textShadow: '0 0 8px currentColor' }}
                >
                  <StatusIcon className="w-4 h-4 inline mr-2" />
                  {statusConfig.label}
                </span>
              </div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {shipment.shipmentNumber}
              </h1>
              {shipment.bolNumber && (
                <p className="text-amber text-sm mt-1 font-mono flex items-center">
                  <FileText className="w-4 h-4 mr-1" />
                  BOL: {shipment.bolNumber}
                </p>
              )}
              <p className="text-text-secondary text-sm mt-1 font-mono">
                Ship Date: {formatDate(shipment.shipDate)}
              </p>
            </div>

            <div className="flex items-center space-x-3 flex-wrap gap-2">
              {/* Print Delivery Ticket */}
              <button
                onClick={handlePrintDeliveryTicket}
                className="flex items-center space-x-2 px-4 py-2 bg-panel-dark hover:bg-steel/20 text-text-secondary hover:text-steel border border-panel-border hover:border-steel transition-all no-print"
              >
                <Printer className="w-4 h-4" />
                <span className="text-xs font-mono uppercase">Print Delivery Ticket</span>
              </button>

              {/* Attach/View BOL */}
              {shipment.bolDocumentUrl ? (
                <a
                  href={shipment.bolDocumentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 px-4 py-2 bg-panel-dark hover:bg-steel/20 text-steel hover:text-amber border border-steel/50 hover:border-amber transition-all no-print"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span className="text-xs font-mono uppercase">View BOL Document</span>
                </a>
              ) : linkedBolId ? (
                <Link
                  to={`/bol/${linkedBolId}`}
                  className="flex items-center space-x-2 px-4 py-2 bg-panel-dark hover:bg-steel/20 text-steel hover:text-amber border border-steel/50 hover:border-amber transition-all no-print"
                >
                  <FileText className="w-4 h-4" />
                  <span className="text-xs font-mono uppercase">View BOL</span>
                </Link>
              ) : (
                <button
                  onClick={() => setShowAttachBolModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-panel-dark hover:bg-steel/20 text-text-secondary hover:text-steel border border-panel-border hover:border-steel transition-all no-print"
                >
                  <Upload className="w-4 h-4" />
                  <span className="text-xs font-mono uppercase">Attach BOL</span>
                </button>
              )}

              {/* Edit button for editable statuses */}
              {isEditable && (
                <Link
                  to={`/shipments/${id}/edit`}
                  className="flex items-center space-x-2 px-4 py-2 bg-panel-dark hover:bg-steel/20 text-text-secondary hover:text-steel border border-panel-border hover:border-steel transition-all no-print"
                >
                  <Edit3 className="w-4 h-4" />
                  <span className="text-xs font-mono uppercase">Edit Shipment</span>
                </Link>
              )}

              {canDelete && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-error-red/20 hover:bg-error-red/30 text-error-red border border-error-red/50 transition-all no-print"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-xs font-mono uppercase">Delete</span>
                </button>
              )}

              {canCancel && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-panel-dark hover:bg-panel-border text-text-secondary border border-panel-border transition-all no-print"
                >
                  <XCircle className="w-4 h-4" />
                  <span className="text-xs font-mono uppercase">Cancel</span>
                </button>
              )}

              {/* Unpost button for POSTED status */}
              {canUnpost && (
                <button
                  onClick={handleUnpost}
                  disabled={isUpdating}
                  className="flex items-center space-x-2 px-4 py-2 bg-panel-dark hover:bg-purple-500/20 text-purple-400 border border-purple-400/50 transition-all disabled:opacity-50 no-print"
                >
                  <Unlock className="w-4 h-4" />
                  <span className="text-xs font-mono uppercase">Unpost</span>
                </button>
              )}

              {/* View Invoice button for INVOICED status */}
              {shipment.status === 'INVOICED' && shipment.invoiceId && (
                <Link
                  to={`/invoices/${shipment.invoiceId}`}
                  className="flex items-center space-x-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-400/50 transition-all no-print"
                >
                  <Receipt className="w-4 h-4" />
                  <span className="text-xs font-mono uppercase">View Invoice</span>
                </Link>
              )}

              {/* Primary action button based on status */}
              {statusConfig.nextAction && (
                <button
                  onClick={() => {
                    if (statusConfig.nextStatus === 'DELIVERED') {
                      setShowDeliveryModal(true);
                    } else if (statusConfig.nextStatus === 'VERIFIED') {
                      handleVerify();
                    } else if (statusConfig.nextStatus === 'POSTED') {
                      handlePost();
                    } else if (statusConfig.nextStatus === 'INVOICED') {
                      // TODO: Navigate to invoice creation with shipment pre-selected
                      navigate(`/invoices/new?shipmentId=${id}`);
                    } else {
                      handleStatusUpdate(statusConfig.nextStatus!);
                    }
                  }}
                  disabled={isUpdating}
                  className="flex items-center space-x-2 px-6 py-2 bg-amber hover:bg-amber/90 text-background font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50 no-print"
                  style={{ boxShadow: '0 0 15px rgba(255, 149, 0, 0.4)' }}
                >
                  <Play className="w-5 h-5" />
                  <span>{statusConfig.nextAction}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Editability Status Banner */}
      <div className={`p-3 border ${isEditable
        ? 'bg-blue-900/30 border-blue-500'
        : 'bg-red-900/30 border-red-500'} no-print`}
      >
        {isEditable ? (
          <div className="flex items-center space-x-2">
            <Edit3 className="w-5 h-5 text-blue-400" />
            <span className="text-blue-400 font-mono text-sm">
              This shipment can be edited. All fields are modifiable until posted.
            </span>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <Lock className="w-5 h-5 text-red-400" />
            <span className="text-red-400 font-mono text-sm">
              This shipment is locked ({shipment.status.toLowerCase()}).
              {shipment.invoiceId
                ? ' To make changes, void the invoice first.'
                : shipment.status === 'POSTED'
                  ? ' Click "Unpost" to enable editing.'
                  : ''}
            </span>
          </div>
        )}
      </div>

      {/* Progress Timeline */}
      <div className="bg-panel-dark border border-panel-border p-6 no-print">
        <h3 className="text-sm font-mono text-text-tertiary uppercase tracking-wider mb-4">
          Shipment Progress
        </h3>
        <div className="flex items-center justify-between">
          {['PENDING', 'IN_TRANSIT', 'DELIVERED', 'VERIFIED', 'POSTED', 'INVOICED'].map((status, index, arr) => {
            const config = STATUS_CONFIG[status]!;
            const Icon = config.icon;
            const statusOrder = ['PENDING', 'IN_TRANSIT', 'DELIVERED', 'VERIFIED', 'POSTED', 'INVOICED'];
            const currentStatusIndex = statusOrder.indexOf(shipment.status);
            const isActive = shipment.status === status;
            const isPast = currentStatusIndex > index ||
              (shipment.status === 'PARTIAL' && index <= 2);
            const isFailed = shipment.status === 'FAILED' || shipment.status === 'CANCELLED';

            return (
              <div key={status} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                      isActive
                        ? `${config.bgColor} ${config.color} border-current`
                        : isPast
                          ? 'bg-success-green/20 border-success-green text-success-green'
                          : isFailed
                            ? 'bg-panel-border/20 border-panel-border text-text-tertiary'
                            : 'bg-panel-border/20 border-panel-border text-text-tertiary'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span
                    className={`text-xs font-mono mt-2 ${
                      isActive ? config.color : isPast ? 'text-success-green' : 'text-text-tertiary'
                    }`}
                  >
                    {config.label}
                  </span>
                </div>
                {index < arr.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      isPast ? 'bg-success-green' : 'bg-panel-border'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Shipment Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer & Delivery */}
          <InfoPanel title="Delivery Information" icon={MapPin}>
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-panel-border">
              <div>
                <p className="text-text-primary text-lg">{shipment.customer.name}</p>
                <p className="text-text-tertiary text-sm font-mono">
                  {shipment.customer.customerNumber}
                </p>
              </div>
              {shipment.customer.phone && (
                <a
                  href={`tel:${shipment.customer.phone}`}
                  className="flex items-center space-x-2 text-steel hover:text-amber transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span className="font-mono">{shipment.customer.phone}</span>
                </a>
              )}
            </div>

            {/* Delivery Address */}
            <div className="mb-4">
              <p className="text-text-tertiary text-xs uppercase font-mono mb-1">
                Delivery Address
              </p>
              <div className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 text-amber mt-1" />
                <div>
                  <p className="text-text-primary">{shipment.deliveryAddress || 'N/A'}</p>
                  <p className="text-text-secondary">
                    {shipment.deliveryCity}, {shipment.deliveryState} {shipment.deliveryZip}
                  </p>
                </div>
              </div>
            </div>

            {/* Order Reference */}
            <div className="p-3 bg-background/50 border border-panel-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-text-tertiary text-xs uppercase font-mono mb-1">
                    Order Reference
                  </p>
                  <Link
                    to={`/orders/${shipment.order.id}`}
                    className="text-amber font-mono hover:underline"
                  >
                    {shipment.order.orderNumber}
                  </Link>
                </div>
                {shipment.order.poNumber && (
                  <div>
                    <p className="text-text-tertiary text-xs uppercase font-mono mb-1">
                      PO Number
                    </p>
                    <p className="text-text-primary font-mono">{shipment.order.poNumber}</p>
                  </div>
                )}
              </div>
            </div>
          </InfoPanel>

          {/* Shipment Items */}
          <InfoPanel title="Shipment Items" icon={Package}>
            <table className="w-full">
              <thead>
                <tr className="text-left text-text-tertiary text-xs uppercase font-mono">
                  <th className="pb-2">Product</th>
                  <th className="pb-2 text-right">Shipped</th>
                  <th className="pb-2 text-right">Delivered</th>
                  <th className="pb-2 text-right">Price</th>
                  <th className="pb-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {shipment.items.map((item) => (
                  <tr key={item.id} className="border-t border-panel-border">
                    <td className="py-3">
                      <p className="text-text-primary">{item.product.name}</p>
                      <p className="text-text-tertiary text-xs font-mono">{item.product.sku}</p>
                      {/* WHERE THIS FUEL CAME FROM. Tax follows the movement,
                          so a load lifted in another state owes that state —
                          and without an origin the engine assumes it never
                          crossed a line. Shown per line because one shipment
                          can carry two products from two racks. */}
                      <div className="mt-1">
                        <LiftedAtCell
                          item={item}
                          shipmentStatus={shipment.status}
                          onChanged={fetchShipment}
                        />
                      </div>
                    </td>
                    <td className="py-3 text-right text-text-primary font-mono">
                      {item.shippedQuantity.toLocaleString()} {item.unit}
                    </td>
                    <td className="py-3 text-right font-mono">
                      {item.deliveredQuantity !== null ? (
                        <span
                          className={
                            item.deliveredQuantity === item.shippedQuantity
                              ? 'text-success-green'
                              : 'text-yellow-400'
                          }
                        >
                          {item.deliveredQuantity.toLocaleString()} {item.unit}
                        </span>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="py-3 text-right text-text-secondary font-mono">
                      ${item.unitPrice.toFixed(2)}
                    </td>
                    <td className="py-3 text-right text-text-primary font-mono">
                      ${item.lineTotal.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-amber">
                  <td className="py-3 text-text-tertiary font-mono">Totals</td>
                  <td className="py-3 text-right text-text-primary font-mono">
                    {totalShipped.toLocaleString()} gal
                  </td>
                  <td className="py-3 text-right font-mono">
                    {totalDelivered > 0 ? (
                      <span
                        className={
                          totalDelivered === totalShipped ? 'text-success-green' : 'text-yellow-400'
                        }
                      >
                        {totalDelivered.toLocaleString()} gal
                      </span>
                    ) : (
                      <span className="text-text-tertiary">—</span>
                    )}
                  </td>
                  <td colSpan={2} className="py-3 text-right text-amber font-display text-lg">
                    ${totalValue.toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </InfoPanel>

          {/* Delivery Confirmation */}
          {(shipment.status === 'DELIVERED' || shipment.status === 'PARTIAL') && (
            <InfoPanel title="Delivery Confirmation" icon={CheckCircle}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-text-tertiary text-xs uppercase font-mono mb-1">
                    Delivery Date
                  </p>
                  <p className="text-text-primary font-mono">
                    {shipment.deliveryDate ? formatDate(shipment.deliveryDate) : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-text-tertiary text-xs uppercase font-mono mb-1">
                    Delivery Time
                  </p>
                  <p className="text-text-primary font-mono">
                    {formatTime(shipment.deliveryDate)}
                  </p>
                </div>
                <div>
                  <p className="text-text-tertiary text-xs uppercase font-mono mb-1">
                    Total Delivered
                  </p>
                  <p className="text-success-green font-mono text-lg">
                    {totalDelivered.toLocaleString()} gal
                  </p>
                </div>
                {shipment.receivedBy && (
                  <div>
                    <p className="text-text-tertiary text-xs uppercase font-mono mb-1">
                      Received By
                    </p>
                    <p className="text-text-primary">{shipment.receivedBy}</p>
                  </div>
                )}
              </div>
              {shipment.deliveryNotes && (
                <div className="mt-4 pt-4 border-t border-panel-border">
                  <p className="text-text-tertiary text-xs uppercase font-mono mb-1">
                    Delivery Notes
                  </p>
                  <p className="text-text-secondary">{shipment.deliveryNotes}</p>
                </div>
              )}
            </InfoPanel>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Source Tank */}
          {shipment.sourceTank && (
            <InfoPanel title="Source Tank" icon={Database}>
              <div className="text-center py-4">
                <Database className="w-12 h-12 mx-auto text-steel mb-3" />
                <p className="text-steel font-mono text-lg">{shipment.sourceTank.tankNumber}</p>
                <p className="text-text-primary">{shipment.sourceTank.name}</p>
                {shipment.sourceTank.product && (
                  <p className="text-text-secondary text-sm mt-1">
                    {shipment.sourceTank.product.name}
                  </p>
                )}
              </div>
            </InfoPanel>
          )}

          {/* Truck Info */}
          {shipment.truck && (
            <InfoPanel title="Assigned Truck" icon={Truck}>
              <div className="text-center py-4">
                <Truck className="w-12 h-12 mx-auto text-amber mb-3" />
                <p className="text-amber font-mono text-lg">{shipment.truck.unitNumber}</p>
                {shipment.truck.name && (
                  <p className="text-text-primary">{shipment.truck.name}</p>
                )}
                <div className="mt-3 text-text-secondary text-sm space-y-1">
                  {shipment.truck.make && shipment.truck.model && (
                    <p>
                      {shipment.truck.make} {shipment.truck.model}
                    </p>
                  )}
                  {shipment.truck.licensePlate && (
                    <p className="font-mono">{shipment.truck.licensePlate}</p>
                  )}
                  {shipment.truck.totalCapacity && (
                    <p className="text-amber">
                      {shipment.truck.totalCapacity.toLocaleString()} gal capacity
                    </p>
                  )}
                </div>
              </div>
            </InfoPanel>
          )}

          {/* Driver Info */}
          {shipment.driver && (
            <InfoPanel title="Assigned Driver" icon={User}>
              <div className="text-center py-4">
                <User className="w-12 h-12 mx-auto text-steel mb-3" />
                <p className="text-steel font-mono text-lg">{shipment.driver.driverNumber}</p>
                <p className="text-text-primary">
                  {shipment.driver.firstName} {shipment.driver.lastName}
                </p>
                {shipment.driver.phone && (
                  <a
                    href={`tel:${shipment.driver.phone}`}
                    className="flex items-center justify-center space-x-2 text-amber hover:underline mt-2"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="font-mono">{shipment.driver.phone}</span>
                  </a>
                )}
              </div>
            </InfoPanel>
          )}

          {/* Dispatch Reference */}
          {shipment.dispatch && (
            <InfoPanel title="Dispatch Reference" icon={Send}>
              <div className="text-center py-4">
                <Link
                  to={`/dispatch/${shipment.dispatch.id}`}
                  className="text-amber font-mono text-lg hover:underline"
                >
                  {shipment.dispatch.dispatchNumber}
                </Link>
                <p
                  className={`text-sm mt-1 ${STATUS_CONFIG[shipment.dispatch.status]?.color || 'text-text-secondary'}`}
                >
                  {STATUS_CONFIG[shipment.dispatch.status]?.label || shipment.dispatch.status}
                </p>
              </div>
            </InfoPanel>
          )}

          {/* Shipment Notes */}
          {shipment.notes && (
            <InfoPanel title="Shipment Notes" icon={FileText}>
              <p className="text-text-secondary whitespace-pre-wrap">{shipment.notes}</p>
            </InfoPanel>
          )}
        </div>
      </div>

      {/* Print-only Signature Block */}
      <div className="hidden print:block" style={{ marginTop: '3rem', pageBreakInside: 'avoid' }}>
        <div style={{ display: 'flex', gap: '3rem', marginTop: '2rem' }}>
          <div style={{ flex: 1 }}>
            <div style={{ borderBottom: '1px solid black', height: '40px', marginBottom: '4px' }} />
            <p style={{ fontSize: '9pt' }}>Customer Signature</p>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ borderBottom: '1px solid black', height: '40px', marginBottom: '4px' }} />
            <p style={{ fontSize: '9pt' }}>Print Name</p>
          </div>
          <div style={{ width: '150px' }}>
            <div style={{ borderBottom: '1px solid black', height: '40px', marginBottom: '4px' }} />
            <p style={{ fontSize: '9pt' }}>Date</p>
          </div>
        </div>
        {shipment.notes && (
          <div style={{ marginTop: '1.5rem' }}>
            <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>Notes:</p>
            <p>{shipment.notes}</p>
          </div>
        )}
      </div>

      {/* Delivery Confirmation Modal */}
      {showDeliveryModal && (
        <Modal
          title="Confirm Delivery"
          onClose={() => setShowDeliveryModal(false)}
          onConfirm={() => handleStatusUpdate('DELIVERED')}
          confirmText="Confirm Delivery"
          isLoading={isUpdating}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-text-secondary text-xs uppercase font-mono mb-2">
                Total Quantity Delivered (gallons)
              </label>
              <input
                type="number"
                value={deliveredQuantity}
                onChange={(e) => setDeliveredQuantity(e.target.value)}
                placeholder={totalShipped.toString()}
                className="form-input"
              />
              <p className="text-text-tertiary text-xs mt-1">
                Shipped: {totalShipped.toLocaleString()} gallons
              </p>
            </div>
            <div>
              <label className="block text-text-secondary text-xs uppercase font-mono mb-2">
                Received By
              </label>
              <input
                type="text"
                value={receivedBy}
                onChange={(e) => setReceivedBy(e.target.value)}
                placeholder="Customer name"
                className="form-input"
              />
            </div>
            <div>
              <label className="block text-text-secondary text-xs uppercase font-mono mb-2">
                Delivery Notes
              </label>
              <textarea
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                rows={3}
                placeholder="Any notes about the delivery..."
                className="form-input resize-none"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <Modal
          title="Cancel Shipment"
          onClose={() => setShowCancelModal(false)}
          onConfirm={() => handleStatusUpdate('CANCELLED')}
          confirmText="Cancel Shipment"
          confirmColor="error"
          isLoading={isUpdating}
        >
          <p className="text-text-secondary mb-4">
            Are you sure you want to cancel this shipment?
          </p>
          <div>
            <label className="block text-text-secondary text-xs uppercase font-mono mb-2">
              Cancellation Reason
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Why is this shipment being cancelled?"
              className="form-input resize-none"
            />
          </div>
        </Modal>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <Modal
          title="Delete Shipment"
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          confirmText="Delete Permanently"
          confirmColor="error"
          isLoading={isUpdating}
        >
          <div className="flex items-start space-x-4">
            <AlertTriangle className="w-12 h-12 text-error-red flex-shrink-0" />
            <div>
              <p className="text-text-primary mb-2">
                Are you sure you want to delete shipment{' '}
                <span className="text-amber font-mono">{shipment.shipmentNumber}</span>?
              </p>
              <p className="text-text-secondary text-sm">This action cannot be undone.</p>
            </div>
          </div>
        </Modal>
      )}

      {/* Attach BOL Modal */}
      {showAttachBolModal && (
        <Modal
          title="Attach Bill of Lading"
          onClose={() => {
            setShowAttachBolModal(false);
            setBolDocumentUrl('');
          }}
          onConfirm={handleAttachBol}
          confirmText="Attach BOL"
          isLoading={isUpdating}
        >
          <div className="space-y-4">
            <p className="text-text-secondary text-sm">
              Enter the URL or file path to the BOL document received from the terminal or carrier.
            </p>
            <div>
              <label className="block text-text-secondary text-xs uppercase font-mono mb-2">
                BOL Document URL
              </label>
              <input
                type="text"
                value={bolDocumentUrl}
                onChange={(e) => setBolDocumentUrl(e.target.value)}
                placeholder="https://... or /path/to/bol.pdf"
                className="form-input"
              />
              <p className="text-text-tertiary text-xs mt-1">
                This can be a URL to the document or a file path if stored locally.
              </p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

function InfoPanel({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-4">
          <Icon className="w-5 h-5 text-amber" />
          <h3 className="text-lg font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
        </div>
        {children}
      </div>
    </div>
  );
}

function Modal({
  title,
  onClose,
  onConfirm,
  confirmText,
  confirmColor = 'amber',
  isLoading,
  children,
}: {
  title: string;
  onClose: () => void;
  onConfirm: () => void;
  confirmText: string;
  confirmColor?: 'amber' | 'error';
  isLoading: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-panel-dark border border-panel-border p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-display text-text-primary uppercase tracking-wide mb-4">
          {title}
        </h3>
        {children}
        <div className="flex justify-end space-x-4 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-6 py-2 font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50 ${
              confirmColor === 'error'
                ? 'bg-error-red hover:bg-error-red/90 text-white'
                : 'bg-amber hover:bg-amber/90 text-background'
            }`}
          >
            {isLoading ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShipmentDetailPage;
